import React from "react";
import { Deliverable, ProjectOutline, Workstream } from "tekkr-common/dist/model/project/project";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../../components/ui/carousel";
import { Button } from "../../components/ui/button";
import {
    ClockAlertIcon,
    CalendarIcon,
    ChartGanttIcon,
    CheckIcon,
    ChevronDownIcon,
    DraftingCompassIcon,
    Edit2Icon,
    FileWarningIcon,
    GripVerticalIcon,
    HistoryIcon,
    LayoutTemplateIcon,
    MegaphoneIcon,
    PaperclipIcon,
    PresentationIcon,
    SettingsIcon,
    SquareXIcon,
    Trash2Icon,
    UsersIcon,
} from "lucide-react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link, useSearchParams } from "react-router-dom";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { TekkrMarkdown } from "../../components/shared/markdown/tekkr-markdown";
import { ProjectPageHeader } from "./components/project-page-header";
import Spinner from "../../components/ui/spinner";
import { getTimelineForDeliverables } from "./project-workstream-subpage";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { Skeleton } from "../../components/ui/skeleton";
import { cn } from "../../lib/utils";
import { isDeliverableDelayed } from "../../lib/projects/utils";
import { useProjectProgressQuery } from "../../data/queries/project-progress";
import { ProjectWorkstreamHeader, ProjectWorkstreamIcon } from "../../components/shared/project/workstream-header";
import { StatusCircle, StatusDoneCheck } from "../../components/shared/project/status-circle";
import { isAllDone } from "../../data/project-progress";
import { UserAvatarAndName } from "../../components/shared/org/avatar-for-user";
import { useProject } from "../../data/project";
import { useCurrentOrgUser } from "../../auth/org-provider";
import { useDeleteProjectMutation } from "../../data/mutations/delete-project";
import { useUpdateProjectMutation } from "../../data/mutations/update-project";
import { useUpdateWorkstreamMutation } from "../../data/mutations/update-workstream";
import { useUpdateDeliverableMutation } from "../../data/mutations/update-deliverable";
import { useDeleteWorkstreamMutation } from "../../data/mutations/delete-workstream";
import { useDeleteDeliverableMutation } from "../../data/mutations/delete-deliverable";
import { useReorderWorkstreamsMutation } from "../../data/mutations/reorder-workstreams";
import { useReorderDeliverablesMutation } from "../../data/mutations/reorder-deliverables";
import { useConfirmDialog } from "../../components/modals/confirmation-dialog-provider";
import { PlannedTimeline } from "../../components/shared/project/planned-timeline";
import { useProjectSummariesQuery } from "../../data/queries/project-summaries";
import { AssistantLoadingIndicator, MessageContainer } from "../../components/shared/chat/message";
import { AttachmentsDrawer } from "./components/attachments-drawer";
import { useGetProjectNextStepRecommendationQuery } from "../../data/queries/get-project-next-step-recommendation";
import { isProjectCompleted } from "../../lib/projects/utils";
import { useProjectChatUi } from "./components/project-chat";
import { GanttChartDialog } from "../../components/shared/project/gantt-chart-dialog";
import { SaveAsTemplateDialog } from "./components/save-as-template-dialog";
import { AssignOwnersDialog, hasUnassignedOwners } from "./components/assign-owners-dialog";
import { FileAttachmentIndicator } from "./components/file-attachment-indicator";
import Contenteditable from "../../components/shared/contenteditable";

type OnSelected = (workstreamId: string, deliverableId?: string) => void;

function SlideCountIndicator({ slideCount, className }: { slideCount: number; className?: string }) {
    function Item({ maxVisibleSlides, className: cn2 }: { maxVisibleSlides: number; className?: string }) {
        if (maxVisibleSlides >= slideCount) {
            return null;
        }
        return (
            <div className={cn(className, cn2)}>
                Showing {maxVisibleSlides} out of {slideCount}
            </div>
        );
    }
    return (
        <>
            <Item maxVisibleSlides={1} className={"block md:hidden"} />
            <Item maxVisibleSlides={2} className={"hidden md:block lg:hidden"} />
            <Item maxVisibleSlides={3} className={"hidden lg:block xl:hidden"} />
            <Item maxVisibleSlides={4} className={"hidden xl:block 2xl:hidden"} />
            <Item maxVisibleSlides={5} className={"hidden 2xl:block"} />
        </>
    );
}

// -- Inline edit helper: tracks local text, saves on blur --

function useInlineEdit(serverValue: string, onSave: (value: string) => void) {
    const [localValue, setLocalValue] = React.useState(serverValue);

    React.useEffect(() => {
        setLocalValue(serverValue);
    }, [serverValue]);

    const handleBlur = () => {
        const trimmed = localValue.trim();
        if (trimmed && trimmed !== serverValue) {
            onSave(trimmed);
        }
    };

    return { value: localValue, onChange: setLocalValue, onBlur: handleBlur };
}

function SortableWorkstreamCard({
    ws,
    wsIndex,
    onSelected,
}: {
    ws: Workstream;
    wsIndex: number;
    onSelected?: OnSelected;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ws.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.85 : undefined,
    };

    // row-span-5: header | hr | owner | description | deliverables
    return (
        <div ref={setNodeRef} style={style} {...attributes} className="row-span-5 grid grid-rows-subgrid">
            <WorkstreamCard
                ws={ws}
                wsIndex={wsIndex}
                onSelected={onSelected}
                isEditing={true}
                dragListeners={listeners}
                layout="subgrid"
            />
        </div>
    );
}

function SortableDeliverableRow({
    d,
    dIndex,
    ws,
    isDone,
    onSelected,
}: {
    d: Deliverable;
    dIndex: number;
    ws: Workstream;
    isDone: boolean;
    onSelected?: OnSelected;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: d.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.85 : undefined,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <DeliverableRow
                d={d}
                dIndex={dIndex}
                ws={ws}
                isDone={isDone}
                onSelected={onSelected}
                isEditing={true}
                dragListeners={listeners}
            />
        </div>
    );
}

// -- Editable grid with DnD for workstreams --

function EditableOutlineGrid({ outline, onSelected }: { outline: ProjectOutline; onSelected?: OnSelected }) {
    const reorderMutation = useReorderWorkstreamsMutation();
    const [wsOrder, setWsOrder] = React.useState(() => outline.workstreams.map((ws) => ws.id));
    // Sync local order when server data changes (after refetch)
    React.useEffect(() => {
        setWsOrder(outline.workstreams.map((ws) => ws.id));
    }, [outline.workstreams]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor)
    );

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = wsOrder.indexOf(active.id as string);
            const newIndex = wsOrder.indexOf(over.id as string);
            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(wsOrder, oldIndex, newIndex);
                setWsOrder(newOrder);
                reorderMutation.mutate(newOrder);
            }
        }
    }

    const wsMap = new Map(outline.workstreams.map((ws) => [ws.id, ws]));
    const orderedWorkstreams = wsOrder.map((id) => wsMap.get(id)).filter(Boolean) as Workstream[];

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={wsOrder} strategy={verticalListSortingStrategy}>
                <div className={"grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"}>
                    {orderedWorkstreams.map((ws, wsIndex) => (
                        <SortableWorkstreamCard key={ws.id} ws={ws} wsIndex={wsIndex} onSelected={onSelected} />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}

function ProjectOutlineGrid({ outline, ...props }: { outline: ProjectOutline; onSelected?: OnSelected }) {
    return (
        <div className={"grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"}>
            {outline.workstreams.map((ws, wsIndex) => {
                return (
                    <WorkstreamCard
                        key={ws.id}
                        ws={ws}
                        wsIndex={wsIndex}
                        onSelected={props.onSelected}
                        layout="subgrid"
                    />
                );
            })}
        </div>
    );
}

function ProjectOutlineCarousel({ outline, ...props }: { outline: ProjectOutline; onSelected?: OnSelected }) {
    const carouselRef = React.useRef<HTMLDivElement>(null);

    // Equalize card section heights across the carousel (can't use CSS subgrid here because Embla requires flex).
    // Runs when outline changes (owner assigned, deliverable added, description edited, etc.).
    // The resize listener handles viewport changes independently.
    React.useLayoutEffect(() => {
        const container = carouselRef.current;
        if (!container) return;
        const equalize = () => {
            const allElements = container.querySelectorAll<HTMLElement>("[data-equalize]");
            const groups = new Map<string, HTMLElement[]>();
            allElements.forEach((el) => {
                const key = el.getAttribute("data-equalize") || "";
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push(el);
            });
            groups.forEach((elements) => {
                elements.forEach((el) => (el.style.minHeight = ""));
                let max = 0;
                elements.forEach((el) => (max = Math.max(max, el.offsetHeight)));
                elements.forEach((el) => (el.style.minHeight = `${max}px`));
            });
        };
        equalize();
        window.addEventListener("resize", equalize);
        return () => window.removeEventListener("resize", equalize);
    }, [outline]);

    return (
        <Carousel
            opts={{
                align: "start",
            }}
            className={"carousel-global-max-width w-full"}
        >
            <CarouselContent ref={carouselRef}>
                {outline.workstreams.map((ws, wsIndex) => {
                    return (
                        <CarouselItem key={ws.id} className="md:basis-1/2 lg:basis-1/3 xl:basis-1/4 2xl:basis-1/5">
                            <WorkstreamCard ws={ws} wsIndex={wsIndex} onSelected={props.onSelected} />
                        </CarouselItem>
                    );
                })}
            </CarouselContent>
            <CarouselPrevious className="disabled:hidden" />
            <CarouselNext className="disabled:hidden" />
        </Carousel>
    );
}

function WorkstreamCard({
    ws,
    isEditing,
    dragListeners,
    layout = "flex",
    ...props
}: {
    ws: Workstream;
    wsIndex?: number;
    isEditing?: boolean;
    onSelected?: OnSelected;
    dragListeners?: Record<string, unknown>;
    layout?: "subgrid" | "flex";
}) {
    const progress = useProjectProgressQuery();
    const wsStatusCounts = progress.data?.getWorkstreamStatusCounts(ws.id);
    const isDone = wsStatusCounts != null && isAllDone(wsStatusCounts);
    const hasProgress = wsStatusCounts != null && wsStatusCounts.total > wsStatusCounts.todo;
    const showStatusRing = hasProgress && !isDone;

    // Mutations for inline persistence
    const updateWsMutation = useUpdateWorkstreamMutation(ws.id);
    const deleteWsMutation = useDeleteWorkstreamMutation(ws.id);
    const reorderDeliverablesMutation = useReorderDeliverablesMutation(ws.id);
    const confirm = useConfirmDialog();

    // Inline edit state for name and description
    const nameEdit = useInlineEdit(ws.name, (name) => updateWsMutation.mutate({ name }));
    const descriptionEdit = useInlineEdit(ws.description, (description) => updateWsMutation.mutate({ description }));

    // Local deliverable order for smooth DnD
    const [deliverableOrder, setDeliverableOrder] = React.useState(() => ws.deliverables.map((d) => d.id));
    React.useEffect(() => {
        setDeliverableOrder(ws.deliverables.map((d) => d.id));
    }, [ws.deliverables]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor)
    );

    function handleDeliverableDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = deliverableOrder.indexOf(active.id as string);
            const newIndex = deliverableOrder.indexOf(over.id as string);
            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(deliverableOrder, oldIndex, newIndex);
                setDeliverableOrder(newOrder);
                reorderDeliverablesMutation.mutate(newOrder);
            }
        }
    }

    const handleDeleteWorkstream = confirm(
        {
            title: "Delete Workstream",
            body: (
                <div className={"flex flex-col gap-3 pb-3"}>
                    <div>
                        Are you sure you want to delete <strong className={"font-semibold"}>{ws.name}</strong>?
                    </div>
                    <div className={"text-sm text-muted-foreground"}>
                        This will also remove all its deliverables and associated tasks.
                    </div>
                </div>
            ),
            confirmButtonText: "Delete",
            confirmButtonVariant: "destructive",
        },
        () => deleteWsMutation.mutate()
    );

    const dMap = new Map(ws.deliverables.map((d) => [d.id, d]));
    const orderedDeliverables = deliverableOrder.map((id) => dMap.get(id)).filter(Boolean) as Deliverable[];

    // row-span-5: header | hr | owner | description | deliverables
    const containerClassName =
        layout === "subgrid"
            ? cn("row-span-5 grid grid-rows-subgrid gap-y-3 rounded-md border p-3", isDone && "border-status-done/20")
            : cn("flex h-full flex-col gap-3 rounded-md border p-3", isDone && "border-status-done/20");

    return (
        <div className={containerClassName}>
            {/* Row 1: Header (data-equalize attrs are for carousel height equalization only; subgrid ignores them) */}
            <div data-equalize="ws-header" className={"flex flex-row items-start gap-2"}>
                {isEditing && dragListeners && (
                    <div
                        className={"shrink-0 cursor-grab text-muted-foreground hover:text-foreground"}
                        {...dragListeners}
                    >
                        <GripVerticalIcon className={"h-4 w-4"} />
                    </div>
                )}
                <div
                    className={cn(
                        "flex grow flex-row items-center gap-2",
                        !isEditing && "cursor-pointer transition-all hover:scale-[99%]"
                    )}
                    onClick={isEditing ? undefined : () => props.onSelected?.(ws.id)}
                >
                    {isEditing ? (
                        <>
                            <ProjectWorkstreamIcon ws={ws} index={props.wsIndex} />
                            <div
                                className={"grow rounded-md bg-input px-2 py-1 text-base font-medium"}
                                onBlur={nameEdit.onBlur}
                            >
                                <Contenteditable
                                    value={nameEdit.value}
                                    onChange={nameEdit.onChange}
                                    className={"outline-none"}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={cn(isDone && "opacity-65")}>
                                <ProjectWorkstreamHeader active={false} ws={ws} index={props.wsIndex} />
                            </div>
                            <FileAttachmentIndicator target={["workstream-file", ws.id]} />
                        </>
                    )}
                    {!isEditing && isDone && <StatusDoneCheck />}
                    {!isEditing && showStatusRing && wsStatusCounts && (
                        <div className={"shrink-0"}>
                            <StatusCircle counts={wsStatusCounts} size="sm" />
                        </div>
                    )}
                </div>
                {isEditing && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={handleDeleteWorkstream}
                                className={
                                    "shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                                }
                            >
                                <Trash2Icon className={"h-4 w-4"} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>Delete workstream</TooltipContent>
                    </Tooltip>
                )}
            </div>

            {/* Row 2: Separator */}
            <hr />

            {/* Row 3: Owner (always render wrapper for subgrid row) */}
            <div data-equalize="ws-owner">
                {ws.ownerId && (
                    <div className={"flex flex-row items-center gap-1 px-1 text-sm"}>
                        Owner:
                        <UserAvatarAndName
                            avatarFallback={"icon"}
                            userId={ws.ownerId}
                            avatarClassName={"w-5 h-5"}
                            nameClassName={"font-medium"}
                        />
                    </div>
                )}
            </div>

            {/* Row 4: Description */}
            <div data-equalize="ws-description">
                {isEditing ? (
                    <div className={"rounded-md bg-input px-2 py-1 text-sm"} onBlur={descriptionEdit.onBlur}>
                        <Contenteditable
                            value={descriptionEdit.value}
                            onChange={descriptionEdit.onChange}
                            className={"outline-none"}
                        />
                    </div>
                ) : (
                    <div className={cn("line-clamp-4 px-1 text-sm", isDone && "opacity-65")}>{ws.description}</div>
                )}
            </div>

            {/* Row 5: Deliverables */}
            <div className={"flex flex-col gap-3"}>
                {ws.deliverables.length > 0 && (
                    <div className={"px-1 text-xs font-medium text-muted-foreground"}>Deliverables</div>
                )}
                {isEditing ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDeliverableDragEnd}
                    >
                        <SortableContext items={deliverableOrder} strategy={verticalListSortingStrategy}>
                            {orderedDeliverables.map((d, dIndex) => (
                                <SortableDeliverableRow
                                    key={d.id}
                                    d={d}
                                    dIndex={dIndex}
                                    ws={ws}
                                    isDone={isDone}
                                    onSelected={props.onSelected}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                ) : (
                    ws.deliverables.map((d, dIndex) => (
                        <DeliverableRow
                            key={d.id}
                            d={d}
                            dIndex={dIndex}
                            ws={ws}
                            isDone={isDone}
                            onSelected={props.onSelected}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

function DeliverableRow({
    d,
    dIndex,
    ws,
    isDone,
    onSelected,
    isEditing,
    dragListeners,
}: {
    d: Deliverable;
    dIndex: number;
    ws: Workstream;
    isDone: boolean;
    onSelected?: OnSelected;
    isEditing?: boolean;
    dragListeners?: Record<string, unknown>;
}) {
    const progress = useProjectProgressQuery();
    const statusCounts = progress.data?.getDeliverableStatusCounts(ws.id, d.id);
    const deliverableProgress = progress.data?.getDeliverableProgress(ws.id, d.id) ?? 0;
    const hasDeliverableStatusCounts = statusCounts != null && statusCounts.total > 0;
    const { isDelayed, daysOverdue } = isDeliverableDelayed(d, deliverableProgress);

    // Mutations for inline persistence
    const updateDeliverableMutation = useUpdateDeliverableMutation(ws.id, d.id);
    const deleteDeliverableMutation = useDeleteDeliverableMutation(ws.id, d.id);
    const confirm = useConfirmDialog();

    const nameEdit = useInlineEdit(d.name, (name) => updateDeliverableMutation.mutate({ name }));

    const handleDeleteDeliverable = confirm(
        {
            title: "Delete Deliverable",
            body: (
                <div className={"flex flex-col gap-3 pb-3"}>
                    <div>
                        Are you sure you want to delete <strong className={"font-semibold"}>{d.name}</strong>?
                    </div>
                    <div className={"text-sm text-muted-foreground"}>Associated tasks will also be removed.</div>
                </div>
            ),
            confirmButtonText: "Delete",
            confirmButtonVariant: "destructive",
        },
        () => deleteDeliverableMutation.mutate()
    );

    return (
        <div
            className={cn("group flex flex-row items-center gap-3 px-1", !isEditing && "cursor-pointer")}
            onClick={isEditing ? undefined : () => onSelected?.(ws.id, d.id)}
        >
            {/* Drag handle in edit mode */}
            {isEditing && dragListeners && (
                <div className={"shrink-0 cursor-grab text-muted-foreground hover:text-foreground"} {...dragListeners}>
                    <GripVerticalIcon className={"h-3.5 w-3.5"} />
                </div>
            )}

            {/* Number circle */}
            {hasDeliverableStatusCounts && statusCounts ? (
                <StatusCircle counts={statusCounts} size="sm">
                    <div className="flex h-8 w-8 items-center justify-center text-sm font-semibold text-muted-foreground">
                        {dIndex + 1}
                    </div>
                </StatusCircle>
            ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center">
                    <div
                        className={
                            "flex h-8 w-8 items-center justify-center rounded-full border-2 font-semibold text-muted-foreground"
                        }
                    >
                        {dIndex + 1}
                    </div>
                </div>
            )}

            {/* Name: editable or static */}
            {isEditing ? (
                <div className={"grow rounded-md bg-input px-2 py-0.5 text-sm font-medium"} onBlur={nameEdit.onBlur}>
                    <Contenteditable value={nameEdit.value} onChange={nameEdit.onChange} className={"outline-none"} />
                </div>
            ) : (
                <div className={cn("grow text-sm font-medium hover:underline", isDone && "opacity-65")}>{d.name}</div>
            )}

            {/* Status indicators (hidden in edit mode) */}
            {!isEditing && <FileAttachmentIndicator target={["deliverable-file", ws.id, d.id]} />}
            {!isEditing && isDelayed && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <ClockAlertIcon className={"h-5 w-5 shrink-0 text-destructive"} />
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>
                            <span className="font-medium text-destructive">
                                {daysOverdue} {daysOverdue === 1 ? "day " : "days "}
                                overdue
                            </span>
                        </p>
                    </TooltipContent>
                </Tooltip>
            )}
            {!isEditing && deliverableProgress === 1.0 && <StatusDoneCheck />}

            {/* Delete button in edit mode */}
            {isEditing && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={handleDeleteDeliverable}
                            className={
                                "shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                            }
                        >
                            <Trash2Icon className={"h-4 w-4"} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>Delete deliverable</TooltipContent>
                </Tooltip>
            )}
        </div>
    );
}

export function ProjectOverviewSubpage({ onSelected }: { onSelected?: OnSelected }) {
    const { project } = useProject();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentUser = useCurrentOrgUser();
    const [ganttOpen, setGanttOpen] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);

    const updateProjectMutation = useUpdateProjectMutation();

    const deleteProjectMutation = useDeleteProjectMutation();

    const hasTimeline = project.outline.workstreams.some((ws) => ws.deliverables.some((d) => !!d.targetDate));
    const allDeliverables = project.outline.workstreams.flatMap((ws) => ws.deliverables);
    const timeline = getTimelineForDeliverables(allDeliverables);
    const userIsProjectCreator = project.creatorId === currentUser.id;

    const handleDeleteProject = () => {
        if (window.confirm(`Are you sure you want to delete the project "${project.name}"?`)) {
            deleteProjectMutation.mutate(project.id);
        }
    };

    const presentationMode = searchParams.get("presentation") === "true";
    const togglePresentationMode = () => {
        if (presentationMode) {
            searchParams.delete("presentation");
        } else {
            searchParams.set("presentation", "true");
        }
        setSearchParams(searchParams, { replace: true });
    };

    const [attachmentsDrawerOpen, setAttachmentsDrawerOpen] = React.useState(false);
    const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = React.useState(false);
    const [assignOwnersDialogOpen, setAssignOwnersDialogOpen] = React.useState(false);
    const showAssignOwnersProminent = hasUnassignedOwners(project);

    const projectChat = useProjectChatUi();
    const openBackToTheDrawingBoardChat = () => {
        projectChat.startChatWithMessage(
            "I want to make some changes to my project plan. Can you please help me revise it by:\n1. understanding what happened and why I need to change the plan\n2. suggesting an updated project plan to address my concerns?"
        );
    };

    return (
        <div className={"flex flex-col gap-2 p-1"}>
            <div className={"flex flex-col gap-3"}>
                <div className={"flex flex-row items-start justify-between gap-3"}>
                    <ProjectPageHeader
                        onEditName={
                            presentationMode ? undefined : async (name) => updateProjectMutation.mutateAsync({ name })
                        }
                        preheader={"Project"}
                    />

                    {presentationMode && (
                        <Button
                            onClick={togglePresentationMode}
                            variant={"outline"}
                            size={"sm"}
                            className={"self-start text-primary"}
                        >
                            <SquareXIcon className={"w-4.5 h-4.5 me-1"} />
                            Exit presentation view
                        </Button>
                    )}
                </div>

                {presentationMode && <PresentationHeader />}

                {!presentationMode && (
                    <>
                        <div className={"flex flex-row items-center gap-2 text-sm"}>
                            <span className={"text-muted-foreground"}>Project owner:</span>
                            <UserAvatarAndName
                                avatarClassName={"w-5 h-5"}
                                nameClassName={"font-medium"}
                                userId={project.creatorId}
                            />
                        </div>

                        {!hasTimeline ? (
                            <div
                                className={
                                    "mb-1 flex flex-col items-start gap-2 self-start rounded-md border border-primary bg-primary/10 p-3"
                                }
                            >
                                <div className={"flex flex-row items-center gap-2 text-sm font-semibold text-primary"}>
                                    <FileWarningIcon className={"h-4 w-4"} strokeWidth={2.5} />
                                    Missing Timeline
                                </div>
                                <div className={"max-w-prose text-sm opacity-85"}>
                                    Your project doesn't have a timeline yet. A timeline is crucial to get this project
                                    signed off by stakeholders and to track your progress while you're executing. Add
                                    one now to get started.
                                </div>
                                <Link to={"timeline"}>
                                    <Button className={"mt-1"}>
                                        <ChartGanttIcon className={"me-2 h-5 w-5"} />
                                        Add timeline
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className={"-mt-2"}>
                                <PlannedTimeline
                                    {...timeline}
                                    infoTooltip={
                                        "This timeline is derived from the workstream and deliverable timelines."
                                    }
                                />
                            </div>
                        )}

                        <div className={"flex flex-row flex-wrap items-center gap-3"}>
                            {showAssignOwnersProminent && (
                                <Button
                                    size={"sm2"}
                                    className={"bg-primary text-sm text-primary-foreground"}
                                    onClick={() => setAssignOwnersDialogOpen(true)}
                                >
                                    <UsersIcon className={"me-2 h-5 w-5"} />
                                    Assign Owners
                                </Button>
                            )}

                            <Link to={"updates"}>
                                <Button size={"sm2"} variant={"outline"} className={"text-sm"}>
                                    <MegaphoneIcon className={"me-2 h-5 w-5"} />
                                    Share an update
                                </Button>
                            </Link>

                            {hasTimeline && (
                                <Button
                                    size={"sm2"}
                                    variant={"outline"}
                                    className={"text-sm"}
                                    onClick={() => setGanttOpen(true)}
                                >
                                    <ChartGanttIcon className={"me-2 h-5 w-5"} />
                                    Timeline
                                </Button>
                            )}

                            <Button
                                onClick={openBackToTheDrawingBoardChat}
                                size={"sm2"}
                                variant={"outline"}
                                className={"text-sm"}
                            >
                                <DraftingCompassIcon className={"me-2 h-5 w-5"} />
                                Back to the drawing board
                            </Button>

                            <Button
                                size={"sm2"}
                                variant={"outline"}
                                className={"text-sm"}
                                onClick={() => setAttachmentsDrawerOpen(true)}
                            >
                                <PaperclipIcon className={"me-2 h-5 w-5"} />
                                Attachments
                            </Button>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button size={"sm2"} variant={"outline"} className={"text-sm"}>
                                        More <ChevronDownIcon className={"ms-1 h-4 w-4 text-muted-foreground"} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                    <DropdownMenuGroup>
                                        <DropdownMenuItem onClick={togglePresentationMode}>
                                            <PresentationIcon className={"me-2 h-4 w-4"} />
                                            Presentation view
                                        </DropdownMenuItem>
                                    </DropdownMenuGroup>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuGroup>
                                        <Link to={"history"}>
                                            <DropdownMenuItem>
                                                <HistoryIcon className={"me-2 h-4 w-4"} />
                                                Project history
                                            </DropdownMenuItem>
                                        </Link>
                                        <Link to={"meetings"}>
                                            <DropdownMenuItem>
                                                <CalendarIcon className={"me-2 h-4 w-4"} />
                                                Meetings
                                            </DropdownMenuItem>
                                        </Link>
                                    </DropdownMenuGroup>
                                    <DropdownMenuSeparator />
                                    {!showAssignOwnersProminent && (
                                        <DropdownMenuItem onClick={() => setAssignOwnersDialogOpen(true)}>
                                            <UsersIcon className={"me-2 h-4 w-4"} />
                                            Assign Owners
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => setSaveTemplateDialogOpen(true)}>
                                        <LayoutTemplateIcon className={"me-2 h-4 w-4"} />
                                        Save as template
                                    </DropdownMenuItem>
                                    {userIsProjectCreator && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <Link to={"settings"}>
                                                <DropdownMenuItem>
                                                    <SettingsIcon className={"me-2 h-4 w-4"} />
                                                    Settings
                                                </DropdownMenuItem>
                                            </Link>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                onClick={handleDeleteProject}
                                                className={
                                                    "font-medium text-destructive hover:bg-destructive hover:text-white"
                                                }
                                                disabled={deleteProjectMutation.isPending}
                                            >
                                                {deleteProjectMutation.isPending ? (
                                                    <Spinner className={"me-2 h-4 w-4"} />
                                                ) : (
                                                    <Trash2Icon className={"me-2 h-4 w-4"} />
                                                )}
                                                Delete project
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </>
                )}

                {hasTimeline && !presentationMode && <NextUpBox />}

                <div className={"mt-3 flex flex-row items-center gap-3"}>
                    <div className={"font-medium text-muted-foreground"}>Workstreams</div>
                    {!isEditing && !presentationMode && (
                        <SlideCountIndicator
                            slideCount={project.outline.workstreams.length}
                            className={"text-sm opacity-45"}
                        />
                    )}
                    {!presentationMode && (
                        <div className={"ml-auto"}>
                            {isEditing ? (
                                <Button onClick={() => setIsEditing(false)} size={"sm2"} className={"text-sm"}>
                                    <CheckIcon className={"me-1 h-4 w-4"} />
                                    Done
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => setIsEditing(true)}
                                    size={"sm2"}
                                    variant={"outline"}
                                    className={"text-sm"}
                                >
                                    <Edit2Icon className={"me-2 h-5 w-5"} />
                                    Edit
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {isEditing ? (
                    <EditableOutlineGrid outline={project.outline} onSelected={onSelected} />
                ) : presentationMode ? (
                    <ProjectOutlineGrid outline={project.outline} onSelected={onSelected} />
                ) : (
                    <ProjectOutlineCarousel outline={project.outline} onSelected={onSelected} />
                )}

                <AttachmentsDrawer open={attachmentsDrawerOpen} onOpenChange={setAttachmentsDrawerOpen} />
                <GanttChartDialog open={ganttOpen} onOpenChange={setGanttOpen} />
                <SaveAsTemplateDialog
                    projectId={project.id}
                    projectName={project.name}
                    isOpen={saveTemplateDialogOpen}
                    onClose={() => setSaveTemplateDialogOpen(false)}
                />
                <AssignOwnersDialog open={assignOwnersDialogOpen} onOpenChange={setAssignOwnersDialogOpen} />
            </div>
        </div>
    );
}

function PresentationHeader() {
    const { project } = useProject();
    const summariesQuery = useProjectSummariesQuery();
    const workstreamOwners = new Set(project.outline.workstreams.map((ws) => ws.ownerId).filter(Boolean));

    return (
        <div className={"mb-4 flex flex-col gap-3"}>
            <div className={"flex flex-row items-center gap-2 text-sm"}>
                Owned by{" "}
                <UserAvatarAndName
                    avatarClassName={"w-6 h-6"}
                    nameClassName={"font-medium"}
                    userId={project.creatorId}
                />
            </div>

            <div className={"my-2 grid max-w-4xl grid-cols-2 gap-6 text-sm"}>
                <div>
                    <div className={"mb-1 text-xs font-medium text-muted-foreground"}>Project Description</div>
                    {summariesQuery.data ? summariesQuery.data.description : <Skeleton className={"h-12"} />}
                </div>
                <div>
                    <div className={"mb-1 text-xs font-medium text-muted-foreground"}>
                        What happened in the past 2 weeks
                    </div>
                    {summariesQuery.data ? (
                        summariesQuery.data.latestDevelopments ? (
                            <TekkrMarkdown markdown={summariesQuery.data.latestDevelopments} />
                        ) : (
                            <div className="text-muted-foreground">Nothing happened recently.</div>
                        )
                    ) : (
                        <Skeleton className={"h-12"} />
                    )}
                </div>
            </div>

            {workstreamOwners && workstreamOwners.size > 0 && (
                <div className={"flex flex-col gap-2"}>
                    <div className={"text-xs text-muted-foreground"}>Workstream Owners</div>
                    <div className={"flex flex-row flex-wrap items-center gap-3 text-sm"}>
                        {Array.from(workstreamOwners)
                            .filter((o) => !!o)
                            .map((o) => (
                                <div className={"flex flex-row items-center gap-1"} key={o}>
                                    <UserAvatarAndName
                                        avatarFallback={"icon"}
                                        avatarClassName={"w-5 h-5"}
                                        userId={o!}
                                    />
                                </div>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function NextUpBox() {
    const { project } = useProject();
    const progressQuery = useProjectProgressQuery();
    const messageQuery = useGetProjectNextStepRecommendationQuery();
    const projectCompleted = isProjectCompleted(project.outline.workstreams, progressQuery.data);

    if (projectCompleted) {
        return null;
    }

    if (messageQuery.data) {
        return (
            <div className={"my-2"}>
                <MessageContainer role={"assistant"}>
                    <div className={"max-w-prose rounded-xl rounded-tl-none bg-primary/15 p-2 text-sm"}>
                        <div className={"font-semibold"}>Wondering what's next?</div>
                        <TekkrMarkdown
                            markdown={messageQuery.data}
                            className={"animate-in zoom-in-90 slide-in-from-left-5 slide-in-from-top-1.5"}
                        />
                    </div>
                </MessageContainer>
            </div>
        );
    } else {
        return (
            <div className={"my-2"}>
                <AssistantLoadingIndicator />
            </div>
        );
    }
}
